import * as Sentry from '@sentry/browser';
import { Component, type ReactNode } from 'react';

import { ErrorBoundary } from './components/system/ErrorBoundary';
import { CookieConsent } from './components/CookieConsent';
import { SupportChat } from './components/system/SupportChat';
import { AppRouter } from './routes/AppRouter';
import { useWebVitals, useMemoryMonitor } from './lib/performance';

// Note: BackendStatusBanner and TestModeBanner are conditionally imported in AppProviders if needed

// Performance monitoring component
function PerformanceMonitor() {
  useWebVitals(); // Track Core Web Vitals
  useMemoryMonitor(); // Monitor memory usage

  return null; // This component doesn't render anything
}

// Global error boundary with Sentry integration
export class GlobalErrorBoundary extends Component<{ children: ReactNode }, { hasError: boolean }> {
  public override state = { hasError: false };

  public static getDerivedStateFromError(): { hasError: boolean } {
    return { hasError: true };
  }

  public override componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    Sentry.captureException(error, {
      contexts: {
        react: {
          componentStack: errorInfo.componentStack,
        },
      },
    });
  }

  public override render() {
    // Last-resort fallback when the boundary itself catches (e.g. the inner
    // ErrorBoundary throws during its own render). Inline styles so it stays
    // visible even if the CSS/build pipeline is the thing that failed — never
    // render nothing on error.
    if (this.state.hasError) {
      return (
        <div
          role="alert"
          style={{
            minHeight: '100vh',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '1rem',
            padding: '1.5rem',
            textAlign: 'center',
            background: '#0a0a0a',
            color: '#ffffff',
            fontFamily: 'system-ui, sans-serif',
          }}
        >
          <h1 style={{ fontSize: '1.5rem', fontWeight: 700, margin: 0 }}>Something went wrong</h1>
          <p style={{ opacity: 0.7, margin: 0, maxWidth: '32rem' }}>
            The app hit an unexpected error. Please reload the page, or contact{' '}
            <a href="mailto:support@advoltix.com" style={{ color: '#db5c4d' }}>support@advoltix.com</a> if it persists.
          </p>
          <button
            type="button"
            onClick={() => window.location.reload()}
            style={{
              padding: '0.6rem 1.4rem',
              borderRadius: '0.5rem',
              border: 'none',
              background: '#db5c4d',
              color: '#fff',
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            Reload
          </button>
        </div>
      );
    }

    return (
      <ErrorBoundary>
        <PerformanceMonitor />
        {this.props.children}
        <CookieConsent />
        <SupportChat />
      </ErrorBoundary>
    );
  }
}

export function App() {
  return (
    <GlobalErrorBoundary>
      <AppRouter />
    </GlobalErrorBoundary>
  );
}
